import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ComponentProps,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  buildSiteComplianceBusinessUnitOptions,
  buildSiteComplianceDivisionOptions,
  buildSiteComplianceRegionOptions,
  filterSiteComplianceSites,
  getSiteCompliancePrimaryDivisionLabel,
  paginateSiteComplianceSites,
  SITE_COMPLIANCE_SITE_TYPE_LABELS,
  SITE_COMPLIANCE_UNKNOWN_DIVISION,
  type SiteComplianceEvaluation,
  type SiteComplianceSite,
  useSiteComplianceEvaluationsQuery,
  useSitesQuery,
} from '@/domains/siteCompliance';
import { exportToCsv } from '@/lib/utils';
import { ORR_PLATFORMS } from '@/config/constants';
import { AutomationPlatform } from '@/domains/evaluation/evaluation.types';
import { useMobileRouteLoading, useResponsiveViewport } from '@/shared/hooks';
import useToast from '@/shared/stores/toastStore';

import { buildSiteComplianceDetailsPath } from '../../siteComplianceRouting';
import type SiteComplianceContent from './SiteComplianceContent';
import type SiteComplianceFilters from './SiteComplianceFilters';
import type SiteCompliancePagination from './SiteCompliancePagination';
import type RunEvaluationDialog from '../../components/RunEvaluationDialog';
import type {
  SiteComplianceFilters as SiteComplianceFiltersState,
  SiteComplianceListRow,
} from './SiteCompliancePage.types';

const SITE_COMPLIANCE_OPEN_MOBILE_FILTERS_EVENT =
  'site-compliance:open-mobile-filters';

const INITIAL_FILTERS: SiteComplianceFiltersState = {
  businessUnit: '',
  divisions: [],
  enhancedOnly: false,
  flagshipOnly: false,
  limit: 10,
  page: 1,
  region: '',
  siteIds: [],
  siteType: 'ALL',
};

const formatLastReviewedAtForExport = (lastReviewedAt: string | null) => {
  if (!lastReviewedAt) {
    return 'Not Evaluated';
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(new Date(lastReviewedAt));
};

const formatCurrentComplianceForExport = (currentCompliance: number | null) => {
  if (currentCompliance === null || currentCompliance === undefined) {
    return 'Pending';
  }

  return `${currentCompliance}%`;
};

const buildSiteComplianceListRow = (
  site: SiteComplianceSite,
  latestEvaluation?: SiteComplianceEvaluation,
): SiteComplianceListRow => {
  const latestScore = latestEvaluation?.score ?? site.currentCompliance;
  const latestEvaluationId = latestEvaluation?.id ?? site.evaluationId;
  const latestGeneratedAt =
    latestEvaluation?.generatedAt ?? site.lastReviewedAt;

  return {
    ...site,
    currentCompliance: latestScore,
    evaluationId: latestEvaluationId,
    lastEvaluationCheckpointCount: latestEvaluation?.scoredCheckpoints ?? 0,
    lastEvaluationPlatform: latestEvaluation?.platform ?? null,
    lastEvaluationScore: latestScore,
    lastReviewedAt: latestGeneratedAt,
    previousEvaluationScore: latestEvaluation?.previous?.score ?? null,
    previousUpcomingEvaluationScore:
      latestEvaluation?.previous?.inclusiveScore ?? null,
    totalEvaluations: latestEvaluation?.totalEvaluations ?? null,
    upcomingEvaluationCheckpointCount: latestEvaluation?.totalCheckpoints ?? 0,
    upcomingEvaluationScore: latestEvaluation?.inclusiveScore ?? latestScore,
  };
};

const buildEvaluationFallbackSite = (
  evaluation: {
    generatedAt: string;
    id: string;
    score: number;
    siteId: string;
  },
): SiteComplianceSite => ({
  address: null,
  businessUnits: [],
  country: null,
  currentCompliance: evaluation.score,
  evaluationId: evaluation.id,
  isDatacenter: false,
  isEnhanced: false,
  isFlagship: false,
  lastReviewedAt: evaluation.generatedAt,
  networkTier: null,
  primaryDivision: null,
  primaryDivisions: [],
  region: '',
  siteId: evaluation.siteId,
  siteName: evaluation.siteId,
  siteType: 'office',
  source: 'netbox',
  workdayId: evaluation.siteId,
});

interface UseSiteComplianceControllerResult {
  contentProps: ComponentProps<typeof SiteComplianceContent>;
  filtersProps: ComponentProps<typeof SiteComplianceFilters>;
  isExporting: boolean;
  isMobileViewport: boolean;
  onExportSites: () => Promise<void>;
  paginationProps: ComponentProps<typeof SiteCompliancePagination>;
  runEvaluationProps: ComponentProps<typeof RunEvaluationDialog>;
}

export const useSiteComplianceController =
  (): UseSiteComplianceControllerResult => {
    const location = useLocation();
    const navigate = useNavigate();
    const { showToast } = useToast();
    const isMobileViewport = useResponsiveViewport();
    const [filters, setFilters] =
      useState<SiteComplianceFiltersState>(INITIAL_FILTERS);
    const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [runEvaluationSite, setRunEvaluationSite] =
      useState<SiteComplianceListRow | null>(null);

    const siteQueryFilters = useMemo(
      () => ({
        businessUnit: filters.businessUnit || undefined,
        enhanced: filters.enhancedOnly || undefined,
        flagship: filters.flagshipOnly || undefined,
        pageSize: 250,
        primaryDivision:
          filters.divisions[0] &&
          filters.divisions[0] !== SITE_COMPLIANCE_UNKNOWN_DIVISION
            ? filters.divisions[0]
            : undefined,
        region: filters.region || undefined,
        siteId:
          filters.siteIds.length === 1 ? filters.siteIds[0] : undefined,
        siteType: filters.siteType === 'ALL' ? undefined : filters.siteType,
      }),
      [
        filters.businessUnit,
        filters.divisions,
        filters.enhancedOnly,
        filters.flagshipOnly,
        filters.region,
        filters.siteIds,
        filters.siteType,
      ],
    );
    const sitesQuery = useSitesQuery(siteQueryFilters);
    const baseSites = sitesQuery.data?.items ?? [];
    // Unfiltered fetch so the filter dropdowns always list every option,
    // independent of the currently applied filters.
    const allSitesQuery = useSitesQuery({ pageSize: 1000 });
    const latestEvaluationsQuery = useSiteComplianceEvaluationsQuery({
      latestOnly: true,
      pageSize: 50,
    });
    const latestEvaluationsBySiteId = useMemo(
      () =>
        new Map(
          latestEvaluationsQuery.evaluations.map((evaluation) => [
            evaluation.siteId,
            evaluation,
          ]),
        ),
      [latestEvaluationsQuery.evaluations],
    );
    const sites = useMemo(() => {
      const sitesById = new Map<string, SiteComplianceSite>();

      latestEvaluationsQuery.evaluations.forEach((evaluation) => {
        sitesById.set(evaluation.siteId, buildEvaluationFallbackSite(evaluation));
      });

      baseSites.forEach((site) => {
        const latestEvaluation = latestEvaluationsQuery.evaluations.find(
          (evaluation) => evaluation.siteId === site.siteId,
        );

        sitesById.set(site.siteId, {
          ...site,
          ...(latestEvaluation
            ? {
                currentCompliance: latestEvaluation.score,
                evaluationId: latestEvaluation.id,
                lastReviewedAt: latestEvaluation.generatedAt,
              }
            : {}),
        });
      });

      latestEvaluationsQuery.evaluations.forEach((evaluation) => {
        const existingSite = sitesById.get(evaluation.siteId);

        if (existingSite) {
          sitesById.set(evaluation.siteId, {
            ...existingSite,
            currentCompliance: evaluation.score,
            evaluationId: evaluation.id,
            lastReviewedAt: evaluation.generatedAt,
          });
        }
      });

      return Array.from(sitesById.values());
    }, [baseSites, latestEvaluationsQuery.evaluations]);
    const isSitesLoading =
      sitesQuery.isLoading ||
      sitesQuery.isFetching ||
      latestEvaluationsQuery.isLoading ||
      latestEvaluationsQuery.isFetching;

    useMobileRouteLoading(isSitesLoading);

    const divisionOptions = useMemo(
      () => buildSiteComplianceDivisionOptions(allSitesQuery.data?.items ?? []),
      [allSitesQuery.data?.items],
    );
    const businessUnitOptions = useMemo(
      () =>
        buildSiteComplianceBusinessUnitOptions(allSitesQuery.data?.items ?? []),
      [allSitesQuery.data?.items],
    );
    const regionOptions = useMemo(
      () => buildSiteComplianceRegionOptions(allSitesQuery.data?.items ?? []),
      [allSitesQuery.data?.items],
    );
    const siteTypeOptions = useMemo(
      () =>
        Object.entries(SITE_COMPLIANCE_SITE_TYPE_LABELS).map(
          ([value, label]) => ({
            label,
            value: value as SiteComplianceFiltersState['siteType'],
          }),
        ),
      [],
    );
    const siteOptions = useMemo(
      () =>
        [...sites].sort((left, right) => left.siteId.localeCompare(right.siteId)),
      [sites],
    );

    const filteredSites = useMemo(
      () =>
        filterSiteComplianceSites({
          businessUnit: filters.businessUnit,
          divisions: filters.divisions,
          enhancedOnly: filters.enhancedOnly,
          flagshipOnly: filters.flagshipOnly,
          region: filters.region,
          siteIds: filters.siteIds,
          siteType: filters.siteType,
          sites,
        }),
      [filters, sites],
    );

    const totalCount = filteredSites.length;
    const pageCount = Math.max(1, Math.ceil(totalCount / filters.limit));
    const paginatedSites = useMemo(
      () =>
        paginateSiteComplianceSites({
          limit: filters.limit,
          page: filters.page,
          sites: filteredSites,
        }),
      [filteredSites, filters.limit, filters.page],
    );
    const paginatedSiteRows = useMemo(
      () =>
        paginatedSites.map((site) =>
          buildSiteComplianceListRow(
            site,
            latestEvaluationsBySiteId.get(site.siteId),
          ),
        ),
      [latestEvaluationsBySiteId, paginatedSites],
    );

    useEffect(() => {
      if (filters.page <= pageCount) {
        return;
      }

      setFilters((previousFilters) => ({
        ...previousFilters,
        page: pageCount,
      }));
    }, [filters.page, pageCount]);

    useEffect(() => {
      if (!isMobileViewport && isMobileFiltersOpen) {
        setIsMobileFiltersOpen(false);
      }
    }, [isMobileFiltersOpen, isMobileViewport]);

    useEffect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const openMobileFilters = () => {
        if (isMobileViewport) {
          setIsMobileFiltersOpen(true);
        }
      };

      window.addEventListener(
        SITE_COMPLIANCE_OPEN_MOBILE_FILTERS_EVENT,
        openMobileFilters,
      );

      return () => {
        window.removeEventListener(
          SITE_COMPLIANCE_OPEN_MOBILE_FILTERS_EVENT,
          openMobileFilters,
        );
      };
    }, [isMobileViewport]);

    const handleUpdateFilters = useCallback(
      (updates: Partial<SiteComplianceFiltersState>) => {
        setFilters((previousFilters) => ({
          ...previousFilters,
          ...updates,
          page: 1,
        }));
      },
      [],
    );

    const handleSetPage = useCallback((page: number) => {
      setFilters((previousFilters) => ({
        ...previousFilters,
        page,
      }));
    }, []);

    const handleViewSite = useCallback(
      (siteId: string) => {
        navigate(buildSiteComplianceDetailsPath(location.pathname, siteId));
      },
      [location.pathname, navigate],
    );

    const handleRunEvaluation = useCallback(
      (site: SiteComplianceListRow) => {
        setRunEvaluationSite(site);
      },
      [],
    );

    const onExportSites = useCallback(async () => {
      setIsExporting(true);

      try {
        if (filteredSites.length === 0) {
          showToast('warning', {
            title: 'No data to export',
            message: 'No sites match the current filters.',
          });
          return;
        }

        await exportToCsv({
          fileName: `site-compliance-${new Date().toISOString()}`,
          options: {
            fields: [
              {
                label: 'Site ID',
                value: (site: SiteComplianceSite) => site.siteId,
              },
              {
                label: 'Primary Division',
                value: (site: SiteComplianceSite) =>
                  getSiteCompliancePrimaryDivisionLabel(site),
              },
              {
                label: 'Source',
                value: (site: SiteComplianceSite) => {
                  const platform = latestEvaluationsBySiteId.get(site.siteId)
                    ?.platform;
                  if (!platform) return '';
                  const entry =
                    ORR_PLATFORMS[platform as AutomationPlatform] ??
                    ORR_PLATFORMS[AutomationPlatform.UNKNOWN];
                  return entry.label;
                },
              },
              {
                label: 'Last Evaluated At',
                value: (site: SiteComplianceSite) => {
                  const evaluation = latestEvaluationsBySiteId.get(site.siteId);
                  return formatLastReviewedAtForExport(
                    evaluation?.generatedAt ?? site.lastReviewedAt,
                  );
                },
              },
              {
                label: 'Last Evaluation Score',
                value: (site: SiteComplianceSite) => {
                  const evaluation = latestEvaluationsBySiteId.get(site.siteId);
                  return formatCurrentComplianceForExport(
                    evaluation?.score ?? site.currentCompliance,
                  );
                },
              },
              {
                label: 'Upcoming Evaluation Score',
                value: (site: SiteComplianceSite) => {
                  const evaluation = latestEvaluationsBySiteId.get(site.siteId);
                  return formatCurrentComplianceForExport(
                    evaluation?.inclusiveScore ??
                      evaluation?.score ??
                      site.currentCompliance,
                  );
                },
              },
            ],
          },
          rows: filteredSites,
        });
      } catch {
        showToast('error', {
          title: 'Error exporting site compliance',
          message: 'An error occurred while exporting. Please try again.',
        });
      } finally {
        setIsExporting(false);
      }
    }, [filteredSites, latestEvaluationsBySiteId, showToast]);

    return {
      contentProps: {
        emptyStateMessage:
          !isSitesLoading && totalCount === 0
            ? 'No sites match the current filters.'
            : null,
        isMobileViewport,
        isLoading: isSitesLoading,
        onRunEvaluation: handleRunEvaluation,
        onViewSite: handleViewSite,
        sites: paginatedSiteRows,
      },
      filtersProps: {
        businessUnitOptions,
        divisionOptions,
        filters,
        isMobileFiltersOpen,
        isMobileViewport,
        onCloseMobileFilters: () => setIsMobileFiltersOpen(false),
        onUpdateFilters: handleUpdateFilters,
        regionOptions,
        siteOptions,
        siteTypeOptions,
      },
      isExporting,
      isMobileViewport,
      onExportSites,
      paginationProps: {
        isMobileViewport,
        limit: filters.limit,
        onLimitChange: (limit) => {
          handleUpdateFilters({ limit });
        },
        onPageChange: handleSetPage,
        page: filters.page,
        pageCount,
        totalCount,
      },
      runEvaluationProps: {
        isOpen: Boolean(runEvaluationSite),
        onClose: () => setRunEvaluationSite(null),
        siteId: runEvaluationSite?.siteId ?? null,
        siteName: runEvaluationSite?.siteName ?? null,
      },
    };
  };
